import {
  useActivateClassification,
  useClassification,
  useClassificationStatus,
} from '@/api'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { classificationRequestMessage } from '@/components/classification/errors'

// The inbox switch's one confirm: turns processing on with the saved setup.
export function TurnOnDialog({
  inboxId,
  inboxName,
  unsaved,
  onClose,
  onSetUpActions,
}: {
  inboxId: string
  inboxName: string
  unsaved: boolean
  onClose: () => void
  onSetUpActions: () => void
}) {
  const config = useClassification(inboxId)
  const status = useClassificationStatus(inboxId)
  const activate = useActivateClassification(inboxId)
  const version = config.data?.version ?? 0
  const loading = config.isLoading || status.isLoading
  const blocker = loading
    ? null
    : config.isError || status.isError
      ? 'This inbox’s settings couldn’t load. Try again.'
      : unsaved
        ? 'Save or discard your unsaved changes first.'
        : !version
          ? 'Choose what it may do in this inbox first.'
          : !status.data?.activation.ready
            ? (status.data?.activation.reason ?? 'This inbox isn’t ready yet.')
            : null
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !activate.isPending) onClose()
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Turn on processing?</DialogTitle>
          <DialogDescription className="break-all">{inboxName}</DialogDescription>
        </DialogHeader>
        {loading ? (
          <p role="status" className="text-sm text-muted-foreground">
            Checking this inbox…
          </p>
        ) : blocker ? (
          <p className="text-sm">{blocker}</p>
        ) : (
          <p className="text-sm">
            New email will be handled with this inbox’s saved instructions and
            actions.
          </p>
        )}
        {activate.error && (
          <p role="alert" className="text-sm text-destructive">
            {classificationRequestMessage(activate.error)}
          </p>
        )}
        <DialogFooter>
          <Button variant="outline" disabled={activate.isPending} onClick={onClose}>
            Cancel
          </Button>
          {!loading && !version && !unsaved && !config.isError ? (
            <Button onClick={onSetUpActions}>Choose actions</Button>
          ) : (
            <Button
              disabled={loading || !!blocker || activate.isPending}
              onClick={() =>
                activate.mutate(
                  { expected_version: version },
                  { onSuccess: onClose },
                )
              }
            >
              {activate.isPending ? 'Turning on…' : 'Turn on'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
